/**
 * Web Component entry point for the Community Gallery widget.
 *
 * Importing this module registers the <legion-community-gallery> custom element
 * so it can be used in any HTML page or framework that supports web components.
 *
 * @example
 * ```ts
 * import '@legionhandtech/lht-react-widgets/community-gallery-web-component';
 *
 * // Then use in HTML:
 * // <legion-community-gallery api-url="https://your-domain.com" count="4" />
 * ```
 */

import { attachAuthBridge, type AuthBridgeHandle, AUTH_BRIDGE_OBSERVED_ATTRS } from "../shared/auth-bridge";

const GALLERY_CSS_ATTRIBUTES = [
  "gallery-primary-color",
  "gallery-background-color",
  "gallery-text-color",
  "gallery-overlay-bg",
  "gallery-card-radius",
  "gallery-card-shadow",
  "gallery-gap",
  "gallery-hover-scale",
  "gallery-transition-duration",
  "gallery-username-size",
  "gallery-username-color",
  "gallery-icon-size",
  "gallery-font-family",
] as const;

class LegionCommunityGalleryElement extends HTMLElement {
  public iframe: HTMLIFrameElement | null = null;
  private authBridge: AuthBridgeHandle | null = null;

  static get observedAttributes(): string[] {
    return [
      "api-url",
      "count",
      "theme",
      "width",
      "height",
      "show-view-all",
      "view-all-url",
      "community-url",
      ...GALLERY_CSS_ATTRIBUTES,
    
      ...AUTH_BRIDGE_OBSERVED_ATTRS,
    ];
  }

  connectedCallback(): void {
    this.authBridge = attachAuthBridge(this, {
      iframeAccessor: () => this.iframe,
      baseUrl: this.bridgeBaseUrl,
    });
    this.render();
  }

  disconnectedCallback(): void {
    this.authBridge?.detach();
    this.authBridge = null;
  }

  attributeChangedCallback(
    _name: string,
    oldValue: string | null,
    newValue: string | null
  ): void {
    if (oldValue !== newValue) {
      this.render();
    }
  }

  private get apiUrl(): string {
    return this.getAttribute("api-url") || window.location.origin;
  }

  private get count(): number {
    const count = parseInt(this.getAttribute("count") || "4", 10);
    return Math.min(Math.max(count, 1), 12);
  }

  private get theme(): string {
    return this.getAttribute("theme") || "light";
  }

  private get widgetWidth(): string {
    return this.getAttribute("width") || "100%";
  }

  private get widgetHeight(): string {
    return this.getAttribute("height") || "auto";
  }

  private get showViewAll(): boolean {
    return this.getAttribute("show-view-all") !== "false";
  }

  private get viewAllUrl(): string {
    return this.getAttribute("view-all-url") || "/gallery";
  }

  private get communityUrl(): string {
    return this.getAttribute("community-url") || "";
  }

  render(): void {
    this.innerHTML = "";

    const iframe = document.createElement("iframe");
    const url = new URL("/widget/community-gallery", this.apiUrl);

    url.searchParams.set("count", String(this.count));
    url.searchParams.set("theme", this.theme);
    url.searchParams.set("showViewAll", String(this.showViewAll));
    url.searchParams.set("viewAllUrl", this.viewAllUrl);
    if (this.communityUrl) {
      url.searchParams.set("communityUrl", this.communityUrl);
    }

    GALLERY_CSS_ATTRIBUTES.forEach((attr) => {
      const val = this.getAttribute(attr);
      if (val !== null && val !== undefined && String(val).length > 0) {
        url.searchParams.set(attr, val);
      }
    });

    iframe.src = url.toString();
    iframe.style.width = this.widgetWidth;
    iframe.style.height = this.widgetHeight === "auto" ? "200px" : this.widgetHeight;
    iframe.style.border = "none";
    iframe.style.borderRadius = "8px";
    iframe.style.overflow = "hidden";
    iframe.title = "Community Gallery Widget";
    iframe.loading = "lazy";

    this.style.display = "block";
    this.style.width = this.widgetWidth;
    this.style.minHeight = "150px";

    iframe.onload = () => {
      this.dispatchEvent(
        new CustomEvent("load", {
          detail: {
            widget: "community-gallery",
            count: this.count,
            theme: this.theme,
          },
        })
      );
    };

    iframe.onerror = () => {
      this.innerHTML = `<div style="padding:32px;text-align:center;color:#856404;background:#fff3cd;border:1px solid #ffeaa7;border-radius:8px;">Failed to load widget</div>`;
    };

    this.appendChild(iframe);
    this.iframe = iframe;
  }

  private get bridgeBaseUrl(): string {
    return this.apiUrl;
  }
}

if (
  typeof customElements !== "undefined" &&
  !customElements.get("legion-community-gallery")
) {
  customElements.define(
    "legion-community-gallery",
    LegionCommunityGalleryElement
  );
}

export { LegionCommunityGalleryElement };

